import {
	currentTargetRequired,
	onKeyUpEscape,
	qsOptional,
	qsRequired,
	removeTrapFocus,
	trapFocus,
} from '../core/global'
import {
	applyUpdatesFromSectionApi,
	ApplyUpdatesFromSectionAPIInput,
} from '@/scripts/core/features'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class CartDrawer extends UcoastEl {
	static htmlSelector = 'cart-drawer'
	activeElement?: HTMLElement
	constructor() {
		super()

		this.addEventListener(
			'keyup',
			(evt) => evt.code === 'Escape' && this.close()
		)
		this.setOverlayListeners()
		this.setHeaderCartIconAccessibility()
		this.checkAutoOpen()
	}

	checkAutoOpen() {
		const urlParams = new URLSearchParams(window.location.search)
		if (urlParams.get('cart') !== 'open') return
		this.open()
	}

	setHeaderCartIconAccessibility() {
		const cartLink = qsOptional('#cart-icon-bubble')
		if (!cartLink) return
		cartLink.setAttribute('role', 'button')
		cartLink.setAttribute('aria-haspopup', 'dialog')
		cartLink.addEventListener('click', (event) => {
			event.preventDefault()
			this.open(cartLink)
		})
		cartLink.addEventListener('keydown', (event) => {
			if (
				event instanceof KeyboardEvent &&
				event.code.toUpperCase() === 'SPACE'
			) {
				event.preventDefault()
				this.open(cartLink)
			}
		})
	}

	open(triggeredBy?: HTMLElement) {
		const activeMegas = document.querySelectorAll('.mega[open]')
		if (activeMegas && activeMegas.length) return
		if (triggeredBy) this.setActiveElement(triggeredBy)
		const cartDrawerNoteSummary = qsOptional(
			'[id^="Details-"] summary',
			this
		)
		if (
			cartDrawerNoteSummary &&
			!cartDrawerNoteSummary.hasAttribute('role')
		)
			this.setSummaryAccessibility(cartDrawerNoteSummary)
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add('animate', 'active')
		})

		this.addEventListener(
			'transitionend',
			() => {
				const containerToTrapFocusOn = this.classList.contains(
					'is-empty'
				)
					? qsOptional('.drawer__inner-empty', this)
					: qsRequired('#CartDrawer')
				const focusElement =
					qsOptional('.drawer__inner', this) ||
					qsRequired('.drawer__close', this)
				trapFocus(containerToTrapFocusOn, focusElement)
			},
			{ once: true }
		)

		document.body.classList.add('cart-open')
	}

	close() {
		this.classList.remove('active')
		removeTrapFocus(this.activeElement)
		document.body.classList.remove('cart-open')
	}

	setSummaryAccessibility(summary: HTMLElement) {
		summary.setAttribute('role', 'button')
		summary.setAttribute('aria-expanded', 'false')

		if (summary.nextElementSibling?.getAttribute('id')) {
			summary.setAttribute('aria-controls', summary.nextElementSibling.id)
		}

		summary.addEventListener('click', (event: Event) => {
			const currentTarget = currentTargetRequired(event)
			const markExpanded = !currentTarget
				.closest('details')
				?.hasAttribute('open')
			currentTarget.setAttribute(
				'aria-expanded',
				markExpanded ? 'true' : 'false'
			)
		})

		summary.parentElement?.addEventListener('keyup', onKeyUpEscape)
	}

	renderContents(input: ApplyUpdatesFromSectionAPIInput) {
		applyUpdatesFromSectionApi(input)
	}

	setOverlayListeners() {
		const overlay = qsRequired('#CartDrawer-Overlay')
		overlay.addEventListener('click', this.close.bind(this))
	}

	getSectionInnerHTML(html: string, selector = '.shopify-section') {
		const newEl = new DOMParser()
			.parseFromString(html, 'text/html')
			.querySelector(selector)
		if (newEl) return newEl.innerHTML
	}

	setActiveElement(element: HTMLElement) {
		this.activeElement = element
	}
}
